import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { X, Layers, CheckCircle, Clock, AlertCircle } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────
interface SessionOption {
  _id: string;
  formula: string;
  startTime: string;
  endTime: string;
  groupName?: string;
}

interface SessionPickerModalProps {
  sessions: SessionOption[];
  latitude: number;
  longitude: number;
  onSuccess: (status: 'present' | 'late') => void;
  onClose: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function SessionPickerModal({
  sessions,
  latitude,
  longitude,
  onSuccess,
  onClose,
}: SessionPickerModalProps) {
  const { t, i18n } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Format ISO time to 12h
  const formatTime = (raw: string) => {
    try {
      const d = new Date(raw);
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
      }
      return raw;
    } catch {
      return raw;
    }
  };

  const confirmMutation = useMutation({
    mutationFn: (body: { sessionId: string; latitude: number; longitude: number }) =>
      api.post('/student/attendance/confirm', body).then((r) => r.data?.data),
    onSuccess: (data) => {
      onSuccess(data?.status === 'present' ? 'present' : 'late');
    },
    onError: (err: any) => {
      const raw = err?.response?.data?.message || err?.message || t('common.error');
      setErrorMsg(Array.isArray(raw) ? raw.join('\n') : raw);
    },
  });

  const handleConfirm = () => {
    if (!selectedId) return;
    setErrorMsg('');
    confirmMutation.mutate({ sessionId: selectedId, latitude, longitude });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-sm bg-card border border-border/70 rounded-[28px] overflow-hidden shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 end-4 z-20 flex items-center justify-center w-9 h-9 rounded-full bg-muted/50 border border-border/50 text-muted-foreground hover:bg-muted transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex flex-col items-center gap-2 px-6 pt-8 pb-5">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 border border-primary/20 mb-1">
            <Layers className="w-7 h-7 text-primary" strokeWidth={2} />
          </div>
          <h2 className="text-[19px] font-black text-foreground text-center">
            {t('student.attendance.selectSession', { defaultValue: 'اختر المحاضرة' })}
          </h2>
          <p className="text-[12px] text-muted-foreground text-center">
            {t('student.attendance.selectSessionHint', {
              defaultValue: 'يوجد أكثر من محاضرة في هذا الوقت. اختر المحاضرة الخاصة بك.',
            })}
          </p>
        </div>

        {/* Sessions List */}
        <div className="px-4 flex flex-col gap-2.5 max-h-64 overflow-y-auto">
          {sessions.map((session) => {
            const isSelected = selectedId === session._id;
            return (
              <button
                key={session._id}
                onClick={() => setSelectedId(session._id)}
                className={`flex items-center gap-3.5 w-full p-4 rounded-[18px] border text-start transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/10 dark:bg-primary/15'
                    : 'border-border/80 bg-card hover:border-primary/30 hover:bg-primary/5'
                }`}
              >
                {/* Radio */}
                <div
                  className={`shrink-0 flex items-center justify-center w-5 h-5 rounded-full border-2 transition-colors ${isSelected ? 'border-primary' : 'border-border'}`}
                >
                  {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-black text-foreground truncate">
                    {session.formula || t('student.attendance.session', { defaultValue: 'محاضرة' })}
                  </p>
                  {session.groupName && (
                    <p className="text-[12px] text-muted-foreground truncate">
                      {session.groupName}
                    </p>
                  )}
                  <div className="flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3 text-muted-foreground/60" />
                    <p className="text-[11px] text-muted-foreground/70">
                      {formatTime(session.startTime)} – {formatTime(session.endTime)}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Error Banner */}
        {errorMsg && (
          <div className="mx-4 mt-3 flex items-start gap-2 p-3 rounded-xl bg-rose-500/10 border border-rose-500/15">
            <AlertCircle className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
            <p className="text-[12px] text-rose-600 dark:text-rose-400">{errorMsg}</p>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 pt-4 pb-6 flex flex-col gap-2.5 mt-3">
          <button
            onClick={handleConfirm}
            disabled={!selectedId || confirmMutation.isPending}
            className={`flex items-center justify-center gap-2 w-full py-3.5 rounded-[14px] text-[14px] font-black transition-all ${
              selectedId && !confirmMutation.isPending
                ? 'bg-primary hover:bg-primary/90 text-white shadow-md'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            {confirmMutation.isPending ? (
              <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            {confirmMutation.isPending
              ? t('student.attendance.checkingIn', { defaultValue: 'جاري التسجيل...' })
              : t('student.attendance.confirmCheckIn', { defaultValue: 'تأكيد الحضور' })}
          </button>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-full py-3 rounded-[14px] border border-border/80 text-muted-foreground text-[13px] font-bold hover:bg-muted/30 transition-colors"
          >
            {t('common.cancel', { defaultValue: 'إلغاء' })}
          </button>
        </div>
      </div>
    </div>
  );
}
